import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Camera, Upload, X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketPhotoUpload() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Foto troppo grande! Massimo 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setPhoto(base64);
      setPhotoPreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!photo) {
      toast.error('Seleziona una foto');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API_URL}/tickets/${ticketId}/upload-photo`, {
        photo_base64: photo
      });

      toast.success('Foto caricata con successo!');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error) {
      toast.error('Errore durante il caricamento');
      setUploading(false);
    }
  };

  const clearPhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">
              📷 Carica Foto Intestatario
            </CardTitle>
            <p className="text-center text-gray-600 mt-2">
              Per questo biglietto è richiesta una foto dell'intestatario
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>ℹ️ Nota:</strong> La foto sarà visibile solo agli organizzatori durante la scansione del biglietto.
                Assicurati che sia una foto chiara del volto.
              </p>
            </div>

            {!photoPreview ? (
              <div className="space-y-4">
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-blue-500 cursor-pointer transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Clicca per selezionare una foto
                  </p>
                  <p className="text-sm text-gray-600">
                    JPG, PNG, GIF, WebP, SVG - Max 5MB
                  </p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                  size="lg"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Seleziona Foto
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative">
                  <img 
                    src={photoPreview} 
                    alt="Anteprima foto" 
                    className="w-full rounded-lg border-2 border-gray-300"
                  />
                  <Button
                    onClick={clearPhoto}
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleUpload}
                    disabled={uploading}
                    className="flex-1"
                    size="lg"
                  >
                    {uploading ? 'Caricamento...' : 'Conferma e Carica'}
                  </Button>
                  <Button 
                    onClick={clearPhoto}
                    variant="outline"
                    size="lg"
                  >
                    Cambia Foto
                  </Button>
                </div>
              </div>
            )}

            <div className="text-center">
              <p className="text-xs text-gray-500">
                Rispettiamo la tua privacy. La foto sarà utilizzata solo per la verifica all'ingresso.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
